import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight, Play } from "lucide-react";
import TestRequestFormWithCountries from "@/components/TestRequestFormWithCountries";

// Import hero images WebP optimisées (1024x576 ~100KB each)
import heroStreamingHome from "@/assets/iptv-streaming-salon-4k.webp";
import heroCinema from "@/assets/iptv-cinema-maison-4k.webp";
import heroStreamingRoom from "@/assets/iptv-espace-streaming-premium.webp";
import heroSports from "@/assets/iptv-sport-direct-4k.webp";
import heroFuturistic from "@/assets/iptv-interface-catalogue-films.webp";

const heroImages = [
  { src: heroStreamingHome, alt: "Famille regardant le streaming IPTV sur grand écran dans un salon moderne" },
  { src: heroCinema, alt: "Salle de cinéma privée avec écran géant diffusant des films en 4K" },
  { src: heroStreamingRoom, alt: "Espace streaming premium avec ambiance tamisée et écran ultra HD" },
  { src: heroSports, alt: "Match de football en direct diffusé en qualité 4K HDR" },
  { src: heroFuturistic, alt: "Interface streaming futuriste avec sélection de contenus IPTV" },
];

const HeroSection = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>([true, false, false, false, false]);

  // Preload other images after initial render
  useEffect(() => {
    const timer = setTimeout(() => {
      heroImages.slice(1).forEach((imageData, index) => {
        const img = new Image();
        img.src = imageData.src;
        img.onload = () => {
          setImagesLoaded(prev => {
            const newState = [...prev];
            newState[index + 1] = true;
            return newState;
          });
        };
      });
    }, 2000); // Delay preloading of other images

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const scrollToPricing = () => {
    const element = document.querySelector("#pricing");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative min-h-[80vh] md:min-h-[90vh] flex items-center overflow-hidden contain-layout">
      {/* Background Images with Crossfade - Only render current and adjacent for mobile perf */}
      {heroImages.map((imageData, index) => {
        const isCurrent = index === currentImageIndex;
        const isAdjacent = index === (currentImageIndex + 1) % heroImages.length || index === 0;
        if (!isCurrent && !isAdjacent && !imagesLoaded[index]) return null;
        return (
          <div
            key={index}
            className="absolute inset-0 transition-opacity duration-1000 will-change-opacity"
            style={{ opacity: isCurrent ? 1 : 0 }}
          >
            {(index === 0 || imagesLoaded[index]) && (
              <img
                src={imageData.src}
                alt={imageData.alt}
                width={1024}
                height={576}
                className="w-full h-full object-cover"
                loading={index === 0 ? "eager" : "lazy"}
                decoding={index === 0 ? "sync" : "async"}
                fetchPriority={index === 0 ? "high" : "low"}
              />
            )}
          </div>
        );
      })}

      {/* Overlay */}
      <div className="absolute inset-0 hero-overlay" />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-8 md:py-16 pb-28 md:pb-32">
        <div className="max-w-3xl">

          {/* Main Headline */}
          <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-4 md:mb-6">
            <span className="text-primary">Netflix, Disney+, Canal+, beIN Sports</span>{" "}
            et toutes vos chaînes en illimité !
          </h1>

          {/* Subtitle */}
          <p className="text-primary text-sm sm:text-base md:text-lg lg:text-xl mb-3 md:mb-4 font-semibold">
            🏆 Tout le Sport en Direct • 📺 +15 000 Chaînes TV • 🎬 +100 000 Films • 📺 +25 000 Séries
          </p>

          {/* Description */}
          <p className="text-muted-foreground text-sm md:text-lg mb-3 md:mb-4">
            Un seul abonnement pour remplacer Netflix, Prime Video, Disney+, Canal+, beIN Sports, RMC Sport et plus encore !
          </p>
          <p className="text-foreground text-sm md:text-lg mb-6 md:mb-8">
            Accès immédiat après paiement • Compatible tous appareils
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <Button
              onClick={scrollToPricing}
              size="lg"
              className="btn-primary text-white text-base md:text-lg font-semibold px-6 md:px-8 py-5 md:py-6 rounded-lg group w-full sm:w-auto"
            >
              Explorez Nos Abonnements
              <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5 group-hover:translate-x-1 transition-transform" />
            </Button>

            <TestRequestFormWithCountries
              trigger={
                <Button
                  size="lg"
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary/10 text-base md:text-lg font-semibold px-6 md:px-8 py-5 md:py-6 rounded-lg w-full sm:w-auto"
                >
                  <Play className="mr-2 h-4 w-4 md:h-5 md:w-5" />
                  Essayez 24h gratuitement
                </Button>
              }
            />
          </div>

          {/* Stats */}
          <div className="flex flex-wrap gap-6 md:gap-8 lg:gap-12 mt-8 md:mt-12">
            <div>
              <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">15K+</div>
              <div className="text-sm md:text-base text-muted-foreground">Chaînes TV</div>
            </div>
            <div>
              <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">100K+</div>
              <div className="text-sm md:text-base text-muted-foreground">Films</div>
            </div>
            <div>
              <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">25K+</div>
              <div className="text-sm md:text-base text-muted-foreground">Séries</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Banner */}
      <div className="absolute bottom-0 left-0 right-0">
        <div className="container mx-auto px-4 py-4 md:py-6">
          <div className="glass-card rounded-xl py-3 md:py-4 px-4 md:px-6 flex items-center justify-center gap-2 md:gap-3 max-w-2xl mx-auto">
            <div className="check-badge w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center flex-shrink-0">
              <Check className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </div>
            <p className="text-foreground text-center text-sm md:text-lg">
              Recevez vos codes IPTV immédiatement avec ROI DU STREAMING !
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
